import axios from "./axios";

const LOGIN_URL = "/oauth/token";
const SIGNUP_URL = "/users";
const UPDATE_PROFILE_URL = "/users";
const LOGOUT_URL = "/oauth/revoke";
const CURRENT_USER_URL = "/users/me";
const SESSION_CONTEXT_CONFIG = { promangSkipCompanyHeader: true };

const CLIENT_ID = process.env.REACT_APP_CLIENT_ID;
const CLIENT_SECRET = process.env.REACT_APP_CLIENT_SECRET;

// Type definitions for API responses
export interface LoginResponse {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
  error?: string;
  errors?: unknown;
}

export interface UserRoleGrant {
  name: string;
  can: string[];
}

export interface UserData {
  id?: string;
  email?: string;
  roles?: UserRoleGrant[];
  locale?: string;
  createdAt?: string;
  company?: {
    id?: string;
    name?: string;
    active_modules?: string[];
    activeTripletexIntegration?: boolean;
    hours_handler_id?: string | number;
  };
}

export interface UserResponse {
  data?: UserData;
  error?: string;
  status?: number;
}

export interface ApiResponse {
  errors?: unknown;
  error?: string;
  data?: unknown;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
}

export async function createUserWithEmailAndPassword(
  email: string,
  password: string
): Promise<ApiResponse> {
  const data = {
    email: email,
    password: password,
    client_id: CLIENT_ID,
  };

  return axios
    .post(SIGNUP_URL, data)
    .then((response: { data: ApiResponse }) => {
      return response.data as ApiResponse;
    })
    .catch((error: { response?: { data: ApiResponse } }) => {
      return (error.response?.data || {}) as ApiResponse;
    });
}

export async function loginWithEmailAndPassword(
  email: string,
  password: string
): Promise<LoginResponse> {
  const data = {
    grant_type: "password",
    scope: "write",
    email: email,
    password: password,
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
  };

  return axios
    .post(LOGIN_URL, data)
    .then((response: { data: LoginResponse }) => {
      return response.data as LoginResponse;
    })
    .catch((error: { response?: { data: LoginResponse } }) => {
      return (error.response?.data || {}) as LoginResponse;
    });
}

export async function updateUserProfile(
  currentPassword: string,
  token: string | undefined,
  email?: string,
  password?: string
): Promise<ApiResponse> {
  const data = {
    current_password: currentPassword,
    email: email,
    password: password,
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
  };
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  return axios
    .patch(UPDATE_PROFILE_URL, data, config)
    .then((response: { data: ApiResponse }) => {
      return response.data as ApiResponse;
    })
    .catch((error: { response?: { data: ApiResponse } }) => {
      return (error.response?.data || {}) as ApiResponse;
    });
}

export async function logoutUserWithToken(token: string): Promise<ApiResponse> {
  const data = {
    token: token,
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
  };

  return axios
    .post(LOGOUT_URL, data)
    .then((response: { data: ApiResponse }) => {
      return response.data as ApiResponse;
    })
    .catch((error: { response?: { data: ApiResponse } }) => {
      return (error.response?.data || {}) as ApiResponse;
    });
}

export async function requestAccessTokenWithRefreshToken(refreshToken: string): Promise<LoginResponse> {
  const data = {
    grant_type: "refresh_token",
    refresh_token: refreshToken,
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
  };

  return axios
    .post(LOGIN_URL, data)
    .then((response: { data: LoginResponse }) => {
      return response.data as LoginResponse;
    })
    .catch((error: { response?: { data: LoginResponse } }) => {
      return (error.response?.data || {}) as LoginResponse;
    });
}

export async function getCurrentUser(accessToken: string): Promise<UserResponse> {
  const config = {
    ...SESSION_CONTEXT_CONFIG,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return await axios
    .get(CURRENT_USER_URL, config)
    .then((response: { data: UserData; status?: number }) => {
      return { data: response.data, status: response.status } as UserResponse;
    })
    .catch((error: { response?: { data?: UserData; status?: number } }) => {
      return { data: error.response?.data, error: 'Failed to get user', status: error.response?.status } as UserResponse;
    });
}

export async function getCurrentUserSettings(accessToken: string): Promise<UserResponse> {
  const config = {
    ...SESSION_CONTEXT_CONFIG,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return await axios
    .get("/users/me/settings", config)
    .then((response: { data: UserData; status?: number }) => {
      return { data: response.data, status: response.status } as UserResponse;
    })
    .catch((error: { response?: { data?: UserData; status?: number } } | unknown) => {
      if (error && typeof error === 'object' && 'response' in error) {
        const err = error as { response?: { data?: UserData; status?: number } };
        return { data: err.response?.data, error: 'Failed to get settings', status: err.response?.status } as UserResponse;
      }
      return { error: 'Failed to get settings' } as UserResponse;
    });
}
